import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, DollarSign } from 'lucide-react';
import { getAvailabilityColor, getAvailabilityText } from './utils';

function SlotCard({ slot, avail, price, isPast, onBook }) {
  const isFullyBooked = avail && avail.available_courts === 0;
  const isDisabled = isPast;

  return (
    <Card
      className={`p-3 sm:p-5 transition-all ${isPast ? 'opacity-50 bg-muted cursor-not-allowed' : 'cursor-pointer hover:shadow-lg'} ${!isPast && getAvailabilityColor(avail)}`}
      onClick={() => !isDisabled && onBook(slot)}
      data-testid={`time-slot-${slot}`}
    >
      <div className="flex justify-between items-start mb-1.5">
        <div>
          <p className="text-lg sm:text-xl font-bold">{slot}</p>
          <p className="text-xs sm:text-sm font-medium mt-0.5">
            {isPast ? 'Time has passed' : getAvailabilityText(avail)}
          </p>
          {isFullyBooked && !isPast && avail?.waitlist_count > 0 && (
            <p className="text-xs text-purple-600 mt-1">{avail.waitlist_count} in waitlist</p>
          )}
        </div>
        <div className="text-right">
          <Clock className="w-4 h-4 sm:w-5 sm:h-5 opacity-70 mb-1" />
          {price && !isPast && (
            <p className="text-xs font-bold text-primary flex items-center justify-end gap-0.5">
              <DollarSign className="w-3 h-3" />
              {price.price_per_hour}/hr
            </p>
          )}
        </div>
      </div>
      {!isPast && (
        <Button
          size="sm"
          className={`w-full mt-2 h-8 sm:h-9 text-xs sm:text-sm ${isFullyBooked ? 'bg-purple-600 hover:bg-purple-700' : 'btn-primary'}`}
          onClick={(e) => {
            e.stopPropagation();
            onBook(slot);
          }}
        >
          {isFullyBooked ? 'Join Waitlist' : 'Book Now'}
        </Button>
      )}
      {isPast && <p className="text-xs text-muted-foreground mt-2">Not available</p>}
    </Card>
  );
}

export default function TimeSlotGrid({
  selectedLocation, isLocationClosed, timeSlots, loading,
  availability, pricing, isTimeSlotPast, onBook,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold font-heading uppercase">
          <Clock className="w-6 h-6 inline mr-2" /> Available Time Slots
        </h3>
        {loading && <span className="text-sm text-muted-foreground">Loading...</span>}
      </div>

      {isLocationClosed ? (
        <Card className="p-8 text-center">
          <p className="text-xl font-bold text-red-600 mb-2">Location Closed</p>
          <p className="text-muted-foreground">
            {selectedLocation.name} is closed on this day. Please select a different date.
          </p>
        </Card>
      ) : timeSlots.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No time slots available for this date.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {timeSlots.map((slot) => (
            <SlotCard
              key={slot}
              slot={slot}
              avail={availability[slot]}
              price={pricing[slot]}
              isPast={isTimeSlotPast(slot)}
              onBook={onBook}
            />
          ))}
        </div>
      )}
    </div>
  );
}
